"use strict";
/**
 * The EclipseGG admin panel's venue contract (platform spec §4.3-4.5,
 * 2026-10-19): what the seller sends to create, edit, suspend and reactivate
 * a venue, and what the panel is answered.
 *
 * Routes, all under `/api/platform` on the admin host only, behind the
 * platform admin guard (★ = a two-step code typed in the last five minutes):
 *
 *   GET   /venues                          PlatformVenueRow[]
 *   POST  /venues                          createPlatformVenueSchema -> PlatformCreateVenueResult (201)
 *   GET   /venues/:id                      PlatformVenueDetail
 *   PATCH /venues/:id                      updatePlatformVenueSchema -> PlatformVenueDetail
 *   GET   /venues/:id/running-sittings     PlatformRunningSittings
 * ★ POST  /venues/:id/suspend              suspendPlatformVenueSchema -> PlatformSuspendResult
 * ★ POST  /venues/:id/reactivate           reactivatePlatformVenueSchema -> PlatformVenueDetail
 * ★ POST  /venues/:id/end-sittings         endPlatformVenueSittingsSchema -> PlatformEndSittingsResult
 * ★ POST  /venues/:id/reset-owner-password {} -> PlatformOwnerReset
 *   GET   /venues/:id/audit?cursor=&limit= PlatformAuditPage
 *   GET   /settings                        PlatformSettingsDto
 *   PUT   /settings                        updatePlatformSettingsSchema -> PlatformSettingsDto
 *
 * Every date here is a calendar date on the VENUE's own calendar,
 * `YYYY-MM-DD`: `paidUntil` is the last day the venue has paid for, and the
 * sweep suspends it at the start of the day after, in its own time zone.
 *
 * The seller's internal reason (`reason`) goes into the seller's own audit
 * log and nowhere else: the venue's staff see a fixed "suspended" text and a
 * reason-free row in their own audit log, never what the seller typed.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.updatePlatformSettingsSchema = exports.endPlatformVenueSittingsSchema = exports.reactivatePlatformVenueSchema = exports.suspendPlatformVenueSchema = exports.updatePlatformVenueSchema = exports.createPlatformVenueSchema = exports.zTimeZone = exports.zPaidUntil = void 0;
const zod_1 = require("zod");
const app_version_1 = require("./app-version");
const business_day_1 = require("./business-day");
const employees_1 = require("./employees");
const venue_code_1 = require("./venue-code");
/** `2026-02-30` is ten characters of the right shape and no day at all. */
function isRealCalendarDate(value) {
    return (0, business_day_1.isBusinessDate)(value);
}
/**
 * Names Intl knows, spelled as IANA spells them (`Asia/Baghdad`, `UTC`,
 * `America/Argentina/Buenos_Aires`). Intl alone would also take an offset such
 * as `+03:00`, which has no daylight-saving rules and no name a person would
 * recognise on the panel — the business day and every report read this zone,
 * and nothing can edit it after creation, so it must be a real one.
 */
function isIanaTimeZone(value) {
    if (!/^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)*$/.test(value))
        return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
/** The last paid day, `YYYY-MM-DD` on the venue's calendar. */
exports.zPaidUntil = zod_1.z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-12-31.')
    .refine(isRealCalendarDate, 'That date does not exist.');
/** An IANA time zone. Set once, at creation: nothing in the staff app edits it. */
exports.zTimeZone = zod_1.z
    .string()
    .trim()
    .min(1)
    .max(64)
    .refine(isIanaTimeZone, 'Unknown time zone.');
/**
 * Create venue (spec §4.4). The server makes the rest: the owner's temporary
 * password (shown once, in the answer), the Portal actor, the settings row and
 * the four device categories. `paidUntil: null` = not billed (never warned,
 * never suspended automatically). A date already past on the new venue's own
 * calendar is refused unless it is the demo (409 `paid_until_in_past`): the
 * hourly sweep would suspend it before its owner first signed in.
 */
exports.createPlatformVenueSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(120),
    code: venue_code_1.zVenueCode,
    timezone: exports.zTimeZone,
    locale: zod_1.z.enum(['en', 'ar', 'ckb']),
    currencySymbol: zod_1.z.string().trim().min(1).max(8),
    paidUntil: exports.zPaidUntil.nullable(),
    isDemo: zod_1.z.boolean(),
    ownerUsername: employees_1.employeeUsernameSchema,
});
/**
 * Edit venue: any of the three, at least one. `paidUntil: null` stops billing.
 * Extending `paidUntil` never reactivates a suspended venue by itself — that
 * is its own audited action. A date already past on the venue's calendar is
 * refused while the venue trades (409 `paid_until_in_past`): the hourly sweep
 * would suspend it without the fresh code Suspend asks for. Only a changed
 * date is judged; one sent back as it stands is not. While
 * `LEGACY_DEFAULT_VENUE_SLUG` is set, neither this nor Create touches the code
 * it names (409 `venue_code_is_fallback`).
 */
exports.updatePlatformVenueSchema = zod_1.z
    .object({
    name: zod_1.z.string().trim().min(1).max(120).optional(),
    code: venue_code_1.zVenueCode.optional(),
    paidUntil: exports.zPaidUntil.nullable().optional(),
})
    .refine((value) => value.name !== undefined || value.code !== undefined || value.paidUntil !== undefined, 'Nothing to change.');
/**
 * Suspend. `reason` is the seller's own note (required, never shown to the
 * venue). `endRunningSittings` also stops every running sitting once the
 * suspension has landed (spec §3, "Sittings that never end on their own").
 */
exports.suspendPlatformVenueSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(1).max(500),
    endRunningSittings: zod_1.z.boolean().default(false),
});
exports.reactivatePlatformVenueSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().max(500).optional(),
});
exports.endPlatformVenueSittingsSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(1).max(500),
});
/** `null` = no minimum: no app is refused. */
exports.updatePlatformSettingsSchema = zod_1.z.object({
    minAppVersion: app_version_1.zAppVersion.nullable(),
});
//# sourceMappingURL=platform-venues.js.map